import React, { useMemo } from "react";
import "./resumeDoc.css";

/* ---------------------------------------------------------------
   Renders a resume_json as an ATS-plain document.

   Shape (verified against mockinterviewportal_resume_json):
     name, title, summary, location
     contact_info_or_social_media_links: { email, phone, LinkedIn, GitHub, ... }
     candidate_skills: { <group name>: [string] }      groups vary per resume
     experience: [{ company, job_title, dates, location, responsibilities: [] }]
     education:  [{ degree, university, dates }]
     certifications: [string]        projects: [{...}]     both often empty

   Single column, real headings, no tables or columns — the layout an ATS
   parser can actually read, which is the whole point of the document.
   --------------------------------------------------------------- */

/* Everything the tailored pass added or rewrote is highlighted. Comparing
   against the set of original strings catches rewrites and insertions alike
   without needing the server to tell us what it touched. */
function useChangeSet(before) {
  return useMemo(() => {
    const seen = new Set();
    const walk = (v) => {
      if (typeof v === "string") {
        const t = v.trim();
        if (t) seen.add(t);
      } else if (Array.isArray(v)) v.forEach(walk);
      else if (v && typeof v === "object") Object.values(v).forEach(walk);
    };
    walk(before);
    return seen;
  }, [before]);
}

/* Stored summaries join their sentences with ".," and ",," — an artefact of
   how the resume was parsed. Left alone it renders as one broken run-on. */
const tidy = (s) =>
  String(s || "")
    .replace(/\s*\.\s*,\s*/g, ". ")
    .replace(/\s*,\s*,\s*/g, ", ")
    .replace(/\s{2,}/g, " ")
    .trim();

function Line({ text, changed, className }) {
  const t = tidy(text);
  if (!t) return null;
  return <span className={`${className || ""}${changed ? " rd-new" : ""}`.trim()}>{t}</span>;
}

export default function ResumeDoc({ resume, compareTo, page = 1 }) {
  const before = useChangeSet(compareTo);
  const isNew = (s) => {
    if (!compareTo) return false;
    const t = String(s || "").trim();
    return Boolean(t) && !before.has(t);
  };

  if (!resume) return null;

  const contact = resume.contact_info_or_social_media_links || {};
  const contactBits = ["phone", "email", "LinkedIn", "GitHub", "linkedin", "github", "website"]
    .map((k) => contact[k])
    .filter((v, i, a) => v && a.indexOf(v) === i);

  const skillGroups = Object.entries(resume.candidate_skills || {}).filter(
    ([, v]) => Array.isArray(v) && v.length
  );
  const experience = Array.isArray(resume.experience) ? resume.experience : [];
  const education = Array.isArray(resume.education) ? resume.education : [];
  const certifications = (Array.isArray(resume.certifications) ? resume.certifications : []).filter(Boolean);
  const projects = (Array.isArray(resume.projects) ? resume.projects : []).filter(Boolean);

  /* Group name → heading. Resumes come back with snake_case keys. */
  const groupLabel = (k) =>
    String(k).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

  /* Skills come in two shapes: proper groups ({Languages:[...], Frameworks:[...]})
     or one big group whose items are themselves "Category: a, b, c" strings.
     Normalise both into clean labelled rows so Skills never renders as a wall of
     comma-separated text. */
  const skillRows = [];
  skillGroups.forEach(([group, items]) => {
    const arr = (Array.isArray(items) ? items : [items]).map((s) => tidy(s)).filter(Boolean);
    const hasEmbeddedLabels = arr.some((s) => /^[^,]{2,40}:\s/.test(s));
    if (hasEmbeddedLabels) {
      arr.forEach((s) => {
        const m = s.match(/^([^,]{2,40}?):\s*(.+)$/);
        if (m) skillRows.push({ label: m[1].trim(), values: m[2].trim() });
        else skillRows.push({ label: "", values: s });
      });
    } else {
      skillRows.push({ label: skillGroups.length > 1 ? groupLabel(group) : "", values: arr.join(", ") });
    }
  });

  return (
    <article className="rd" aria-label="Resume preview">
      <header className="rd-head">
        <h1 className="rd-name">{resume.name}</h1>
        {resume.title ? <p className="rd-title">{tidy(resume.title)}</p> : null}
        {contactBits.length ? (
          <p className="rd-contact">
            {contactBits.map((v, i) => (
              <React.Fragment key={`${v}-${i}`}>
                {i > 0 ? <span className="rd-sep">|</span> : null}
                <span>{v}</span>
              </React.Fragment>
            ))}
          </p>
        ) : null}
        {resume.location ? <p className="rd-contact">{resume.location}</p> : null}
      </header>

      {resume.summary ? (
        <section className="rd-sec">
          <h2 className="rd-h">Summary</h2>
          <p className="rd-body">
            <Line text={resume.summary} changed={isNew(resume.summary)} />
          </p>
        </section>
      ) : null}

      {skillRows.length ? (
        <section className="rd-sec">
          <h2 className="rd-h">Skills</h2>
          {skillRows.map((row, i) => (
            <p className="rd-skill-row" key={i}>
              {row.label ? <strong className="rd-skill-label">{row.label}: </strong> : null}
              <Line text={row.values} changed={isNew(row.values)} />
            </p>
          ))}
        </section>
      ) : null}

      {experience.length ? (
        <section className="rd-sec">
          <h2 className="rd-h">Experience</h2>
          {experience.map((job, i) => (
            <div className="rd-entry" key={`${job.company}-${i}`}>
              <div className="rd-entry-top">
                <span className="rd-org">{job.company}</span>
                <span className="rd-when">{job.dates}</span>
              </div>
              <div className="rd-entry-top">
                <em className="rd-role">
                  <Line text={job.job_title} changed={isNew(job.job_title)} />
                </em>
                <span className="rd-when">{job.location}</span>
              </div>
              {Array.isArray(job.responsibilities) && job.responsibilities.length ? (
                <ul className="rd-list">
                  {job.responsibilities.map((r, k) => (
                    <li key={k}>
                      <Line text={r} changed={isNew(r)} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {projects.length ? (
        <section className="rd-sec">
          <h2 className="rd-h">Projects</h2>
          {projects.map((p, i) => (
            <div className="rd-entry" key={i}>
              <div className="rd-entry-top">
                <span className="rd-org">{p.name || p.title}</span>
                <span className="rd-when">{p.dates}</span>
              </div>
              {p.description ? (
                <p className="rd-body">
                  <Line text={p.description} changed={isNew(p.description)} />
                </p>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}

      {education.length ? (
        <section className="rd-sec">
          <h2 className="rd-h">Education</h2>
          {education.map((e, i) => (
            <div className="rd-entry" key={i}>
              <div className="rd-entry-top">
                <span className="rd-org">{e.university}</span>
                <span className="rd-when">{e.dates}</span>
              </div>
              <em className="rd-role">{e.degree}</em>
            </div>
          ))}
        </section>
      ) : null}

      {certifications.length ? (
        <section className="rd-sec">
          <h2 className="rd-h">Certifications</h2>
          <ul className="rd-list">
            {certifications.map((c, i) => (
              <li key={i}>
                <Line text={typeof c === "string" ? c : c.name} changed={isNew(typeof c === "string" ? c : c.name)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {page ? <div className="rd-page">{page}</div> : null}
    </article>
  );
}
